import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { L as Link, useLocaleNavigate } from "../lib/i18n";
import { api } from "../lib/api";
import { BRANDS, FUELS, TRANSMISSIONS, BODY_TYPES, FEATURE_CATEGORIES, CONDITIONS, SELLER_TYPES, EURO_NORMS, DRIVETRAINS, EXTERIOR_COLORS, INTERIOR_COLORS, DOOR_OPTIONS, SEAT_OPTIONS, CYLINDER_OPTIONS } from "../lib/constants";
import BrandModelSelect from "../components/BrandModelSelect";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Upload, X, Check, GripVertical, Star, Sparkles, Loader2, Rocket, Crown } from "lucide-react";
import PriceIndicator from "../components/PriceIndicator";
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, KeyboardSensor,
  closestCenter, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy,
  sortableKeyboardCoordinates, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export default function CreateListing() {
  const { t, lang } = useLang();
  const navigate = useLocaleNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);

  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiTitleBusy, setAiTitleBusy] = useState(false);
  const [boostBusy, setBoostBusy] = useState(false);
  const [imagePaths, setImagePaths] = useState([]);
  const [features, setFeatures] = useState([]);
  const [sub, setSub] = useState(null);
  const [subLoaded, setSubLoaded] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null);
  const [currentListing, setCurrentListing] = useState(null); // for boost status display
  const [form, setForm] = useState({
    title: "", brand: "", model: "",
    year: new Date().getFullYear(),
    mileage_km: 0, price_chf: 0,
    fuel: "benzin", transmission: "manuell", body_type: "limousine",
    color: "", power_hp: 0,
    description: "", location: "", phone: "",
    // extended specs
    condition: "occasion", seller_type: "private",
    drivetrain: "", euro_norm: "",
    exterior_color: "", interior_color: "", metallic: false,
    doors: 0, seats: 0, cylinders: 0,
    displacement_cc: 0, fuel_consumption_l100km: 0, co2_g_km: 0,
    battery_capacity_kwh: 0, electric_range_km: 0,
    warranty_months: 0,
    // Phase 68 — extended dealer-grade attributes
    mfk_valid_until: "", ab_mfk: false,
    accident_free: null, non_smoker: null, service_book: null,
    new_tires: false, new_brakes: false,
    last_service_date: "",
    battery_health_pct: 0, number_of_keys: 1,
    warranty_available: null,
    custom_notes: "",
  });

  useEffect(() => {
    if (isEdit) {
      setSubLoaded(true);
      api.getListing(editId).then((c) => {
        setCurrentListing(c);
        setForm({
          title: c.title || "", brand: c.brand || "", model: c.model || "",
          year: c.year || new Date().getFullYear(),
          mileage_km: c.mileage_km || 0, price_chf: c.price_chf || 0,
          fuel: c.fuel || "benzin", transmission: c.transmission || "manuell",
          body_type: c.body_type || "limousine",
          color: c.color || "", power_hp: c.power_hp || 0,
          description: c.description || "", location: c.location || "", phone: c.phone || "",
          condition: c.condition || "occasion", seller_type: c.seller_type || "private",
          drivetrain: c.drivetrain || "", euro_norm: c.euro_norm || "",
          exterior_color: c.exterior_color || "", interior_color: c.interior_color || "",
          metallic: !!c.metallic,
          doors: c.doors || 0, seats: c.seats || 0, cylinders: c.cylinders || 0,
          displacement_cc: c.displacement_cc || 0,
          fuel_consumption_l100km: c.fuel_consumption_l100km || 0,
          co2_g_km: c.co2_g_km || 0,
          battery_capacity_kwh: c.battery_capacity_kwh || 0,
          electric_range_km: c.electric_range_km || 0,
          warranty_months: c.warranty_months || 0,
          // Phase 68
          mfk_valid_until: c.mfk_valid_until || "",
          ab_mfk: !!c.ab_mfk,
          accident_free: c.accident_free ?? null,
          non_smoker: c.non_smoker ?? null,
          service_book: c.service_book ?? null,
          new_tires: !!c.new_tires,
          new_brakes: !!c.new_brakes,
          last_service_date: c.last_service_date || "",
          battery_health_pct: c.battery_health_pct || 0,
          number_of_keys: c.number_of_keys || 0,
          warranty_available: c.warranty_available ?? null,
          custom_notes: c.custom_notes || "",
        });
        setFeatures(c.features || []);
        setImagePaths(c.images || []);
      }).catch(() => { toast.error("Inserat nicht gefunden"); navigate("/dashboard"); });
    } else {
      api.mySubscription().then((r) => { setSub(r.active); setSubLoaded(true); });
    }
    // Intentionally only re-run when the editId actually changes — otherwise
    // server-loaded values would clobber local edits (image reorders, deletes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFeature = (name) => setFeatures((p) => p.includes(name) ? p.filter((x) => x !== name) : [...p, name]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const res = await api.upload(f);
        setImagePaths((p) => [...p, res.path]);
      }
      toast.success(`${files.length} Bild(er) hochgeladen`);
    } catch {
      toast.error("Upload fehlgeschlagen");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const removeImg = (path) => setImagePaths((p) => p.filter((x) => x !== path));

  const generateDescription = async () => {
    if (!form.brand || !form.model) {
      toast.error(t.ai?.needBasics || "Bitte zuerst Marke und Modell ausfüllen.");
      return;
    }
    setAiBusy(true);
    try {
      const res = await api.describeListing({
        locale: lang,
        title: form.title,
        brand: form.brand, model: form.model,
        year: Number(form.year) || 0,
        mileage_km: Number(form.mileage_km) || 0,
        price_chf: Number(form.price_chf) || 0,
        fuel: form.fuel, transmission: form.transmission,
        body_type: form.body_type, drivetrain: form.drivetrain,
        power_hp: Number(form.power_hp) || 0,
        displacement_cc: Number(form.displacement_cc) || 0,
        cylinders: Number(form.cylinders) || 0,
        euro_norm: form.euro_norm,
        condition: form.condition, seller_type: form.seller_type,
        warranty_months: Number(form.warranty_months) || 0,
        doors: Number(form.doors) || 0,
        seats: Number(form.seats) || 0,
        exterior_color: form.exterior_color,
        interior_color: form.interior_color,
        metallic: !!form.metallic,
        fuel_consumption_l100km: Number(form.fuel_consumption_l100km) || 0,
        co2_g_km: Number(form.co2_g_km) || 0,
        battery_capacity_kwh: Number(form.battery_capacity_kwh) || 0,
        electric_range_km: Number(form.electric_range_km) || 0,
        features,
        location: form.location,
        // Phase 68 — feed extended attributes + custom notes to AI
        mfk_valid_until: form.mfk_valid_until || "",
        ab_mfk: !!form.ab_mfk,
        accident_free: form.accident_free,
        non_smoker: form.non_smoker,
        service_book: form.service_book,
        new_tires: !!form.new_tires,
        new_brakes: !!form.new_brakes,
        last_service_date: form.last_service_date || "",
        battery_health_pct: Number(form.battery_health_pct) || 0,
        number_of_keys: Number(form.number_of_keys) || 0,
        warranty_available: form.warranty_available,
        custom_notes: form.custom_notes || "",
      });
      update("description", res.description);
      toast.success(t.ai?.success || "Beschreibung generiert");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (t.ai?.error || "KI-Generierung fehlgeschlagen"));
    } finally {
      setAiBusy(false);
    }
  };

  const suggestTitle = async () => {
    if (!form.brand || !form.model) {
      toast.error(t.ai?.needBasics || "Bitte zuerst Marke und Modell ausfüllen.");
      return;
    }
    setAiTitleBusy(true);
    try {
      const res = await api.suggestTitle({
        locale: lang,
        brand: form.brand, model: form.model,
        year: Number(form.year) || 0,
        power_hp: Number(form.power_hp) || 0,
        fuel: form.fuel, transmission: form.transmission,
        drivetrain: form.drivetrain, body_type: form.body_type,
        condition: form.condition,
        exterior_color: form.exterior_color,
        metallic: !!form.metallic,
        features,
      });
      update("title", res.title);
      toast.success(t.ai?.titleSuccess || "Titel-Vorschlag eingesetzt");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (t.ai?.titleError || "KI-Titel-Generierung fehlgeschlagen"));
    } finally {
      setAiTitleBusy(false);
    }
  };

  const handleBoost = async () => {
    if (!editId) return;
    setBoostBusy(true);
    try {
      const res = await api.createBoostCheckout(editId);
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      throw new Error("No checkout url");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (t.boost?.error || "Boost fehlgeschlagen"));
      setBoostBusy(false);
    }
  };
  const handleCancelBoost = async () => {
    if (!editId) return;
    setBoostBusy(true);
    try {
      await api.cancelBoost(editId);
      const refreshed = await api.getListing(editId);
      setCurrentListing(refreshed);
      toast.success("Boost gestoppt");
    } catch {
      toast.error(t.boost?.error || "Fehler");
    } finally {
      setBoostBusy(false);
    }
  };

  const isBoostActive = !!(currentListing?.featured && currentListing?.featured_until && new Date(currentListing.featured_until) > new Date());
  const boostDaysLeft = currentListing?.featured_until ? Math.max(0, Math.ceil((new Date(currentListing.featured_until) - new Date()) / 86400000)) : 0;

  // dnd-kit sensors: PointerSensor with small distance to allow click-to-delete,
  // TouchSensor with long-press delay so mobile scroll still works.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = (e) => setActiveDragId(e.active.id);
  const handleDragEnd = (e) => {
    setActiveDragId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setImagePaths((p) => {
      const oldIdx = p.indexOf(active.id);
      const newIdx = p.indexOf(over.id);
      if (oldIdx === -1 || newIdx === -1) return p;
      return arrayMove(p, oldIdx, newIdx);
    });
  };
  const handleDragCancel = () => setActiveDragId(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.brand || !form.model) { toast.error("Bitte alle Pflichtfelder ausfüllen"); return; }
    if (!isEdit && !sub) { toast.error(t.pricing.needPlan); navigate("/pricing"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        mileage_km: Number(form.mileage_km),
        price_chf: Number(form.price_chf),
        power_hp: Number(form.power_hp),
        doors: Number(form.doors) || 0,
        seats: Number(form.seats) || 0,
        cylinders: Number(form.cylinders) || 0,
        displacement_cc: Number(form.displacement_cc) || 0,
        fuel_consumption_l100km: Number(form.fuel_consumption_l100km) || 0,
        co2_g_km: Number(form.co2_g_km) || 0,
        battery_capacity_kwh: Number(form.battery_capacity_kwh) || 0,
        electric_range_km: Number(form.electric_range_km) || 0,
        warranty_months: Number(form.warranty_months) || 0,
        metallic: !!form.metallic,
        // Phase 68
        ab_mfk: !!form.ab_mfk,
        new_tires: !!form.new_tires,
        new_brakes: !!form.new_brakes,
        accident_free: form.accident_free,
        non_smoker: form.non_smoker,
        service_book: form.service_book,
        warranty_available: form.warranty_available,
        mfk_valid_until: form.mfk_valid_until || "",
        last_service_date: form.last_service_date || "",
        battery_health_pct: Number(form.battery_health_pct) || 0,
        number_of_keys: Number(form.number_of_keys) || 0,
        custom_notes: form.custom_notes || "",
        images: imagePaths,
        features,
      };
      if (isEdit) {
        const updated = await api.updateListing(editId, payload);
        toast.success("Inserat aktualisiert");
        navigate(`/listings/${updated.id}`);
      } else {
        const created = await api.createListing(payload);
        toast.success("Inserat veröffentlicht");
        navigate(`/listings/${created.id}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402) { toast.error(detail || t.pricing.needPlan); navigate("/pricing"); }
      else toast.error(typeof detail === "string" ? detail : "Fehler beim Speichern");
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">
        {isEdit ? "Inserat bearbeiten" : t.create.title}
      </h1>

      {!isEdit && subLoaded && !sub && (
        <div className="mt-6 flex flex-col gap-4 rounded-lg border border-red-900/60 bg-red-600/5 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="no-plan-banner">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-red-500">{t.pricing.noActive}</p>
            <p className="mt-1 text-sm text-zinc-200">{t.pricing.needPlan}</p>
          </div>
          <Button asChild className="bg-red-600 hover:bg-red-500">
            <Link to="/pricing" data-testid="banner-pricing">{t.pricing.choosePlan}</Link>
          </Button>
        </div>
      )}
      {!isEdit && sub && (
        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-950 px-5 py-3 text-sm text-zinc-300" data-testid="plan-info">
          <span className="font-semibold text-white">{sub.package_name}</span> – {sub.credits_left} / {sub.credits_total} {t.pricing.credits} {t.pricing.remaining.toLowerCase()}
        </div>
      )}

      <form onSubmit={submit} className="mt-10 space-y-12" data-testid="listing-creation-form">
        <Section title={t.create.basic}>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t.create.titleField}</Label>
              <Button
                type="button"
                onClick={suggestTitle}
                disabled={aiTitleBusy || !form.brand || !form.model}
                size="sm"
                data-testid="ai-title-button"
                className="group h-8 gap-1.5 overflow-hidden border border-red-500/40 bg-gradient-to-r from-red-600/15 via-red-500/10 to-orange-500/15 px-3 text-[11px] font-semibold uppercase tracking-wider text-white shadow-[0_0_15px_-8px_rgba(220,38,38,0.6)] transition-all hover:from-red-600/25 hover:via-red-500/20 hover:to-orange-500/25 disabled:opacity-50 disabled:shadow-none"
              >
                {aiTitleBusy ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span data-testid="ai-title-loading">{t.ai?.titleGenerating || "Generiere…"}</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5 text-red-300 transition-transform group-hover:scale-110" />
                    <span>{t.ai?.titleButton || "KI-Titel"}</span>
                  </>
                )}
              </Button>
            </div>
            <Input
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              required
              disabled={aiTitleBusy}
              maxLength={100}
              placeholder={t.ai?.titlePlaceholder || "z.B. BMW M3 Competition 510 PS M-Paket Schwarz"}
              className="border-zinc-800 bg-zinc-950 text-white"
              data-testid="form-title"
            />
          </div>
          <div className="grid gap-5">
            <BrandModelSelect
              brand={form.brand}
              model={form.model}
              onChange={(next) => setForm((f) => ({ ...f, brand: next.brand, model: next.model }))}
              required
              labelBrand={t.create.brand}
              labelModel={t.create.model}
              otherLabel={t.create.modelOther || "Anderes Modell"}
              brandTestId="form-brand"
              modelTestId="form-model"
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <Field label={t.create.year}><Input type="number" value={form.year} onChange={(e) => update("year", e.target.value)} className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-year" /></Field>
            <Field label={t.create.km}><Input type="number" value={form.mileage_km} onChange={(e) => update("mileage_km", e.target.value)} className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-km" /></Field>
            <Field label={t.create.price}><Input type="number" value={form.price_chf} onChange={(e) => update("price_chf", e.target.value)} className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-price" /></Field>
          </div>
          <PriceIndicator
            brand={form.brand}
            model={form.model}
            year={form.year}
            mileageKm={form.mileage_km}
            powerHp={form.power_hp}
            priceChf={form.price_chf}
            excludeId={editId}
          />
        </Section>

        <Section title={t.create.engine}>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label={t.create.fuel}>
              <Select value={form.fuel} onValueChange={(v) => update("fuel", v)}>
                <SelectTrigger className="border-zinc-800 bg-zinc-950 capitalize text-white" data-testid="form-fuel"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-950 text-white">{FUELS.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t.create.trans}>
              <Select value={form.transmission} onValueChange={(v) => update("transmission", v)}>
                <SelectTrigger className="border-zinc-800 bg-zinc-950 capitalize text-white" data-testid="form-transmission"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-950 text-white">{TRANSMISSIONS.map((tr) => <SelectItem key={tr} value={tr} className="capitalize">{tr}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <Field label={t.create.body}>
              <Select value={form.body_type} onValueChange={(v) => update("body_type", v)}>
                <SelectTrigger className="border-zinc-800 bg-zinc-950 capitalize text-white" data-testid="form-body"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-950 text-white">{BODY_TYPES.map((b) => <SelectItem key={b} value={b} className="capitalize">{b}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Antrieb">
              <Select value={form.drivetrain || "none"} onValueChange={(v) => update("drivetrain", v === "none" ? "" : v)}>
                <SelectTrigger className="border-zinc-800 bg-zinc-950 capitalize text-white" data-testid="form-drivetrain"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-950 text-white">
                  <SelectItem value="none">—</SelectItem>
                  {DRIVETRAINS.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Euro-Norm">
              <Select value={form.euro_norm || "none"} onValueChange={(v) => update("euro_norm", v === "none" ? "" : v)}>
                <SelectTrigger className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-euro"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-950 text-white">
                  <SelectItem value="none">—</SelectItem>
                  {EURO_NORMS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid gap-5 sm:grid-cols-4">
            <Field label="Leistung PS">
              <Input type="number" value={form.power_hp} onChange={(e) => update("power_hp", e.target.value)} className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-power" />
              {form.power_hp > 0 && <p className="text-[10px] text-zinc-500">≈ {Math.round(Number(form.power_hp) * 0.7355)} kW</p>}
            </Field>
            <Field label="Hubraum (cm³)">
              <Input type="number" value={form.displacement_cc} onChange={(e) => update("displacement_cc", e.target.value)} className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-displacement" />
            </Field>
            <Field label="Zylinder">
              <Select value={form.cylinders ? String(form.cylinders) : "0"} onValueChange={(v) => update("cylinders", v === "0" ? 0 : Number(v))}>
                <SelectTrigger className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-cylinders"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-950 text-white">
                  <SelectItem value="0">—</SelectItem>
                  {CYLINDER_OPTIONS.map((c) => <SelectItem key={c} value={String(c)}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Garantie (Mt.)">
              <Input type="number" value={form.warranty_months} onChange={(e) => update("warranty_months", e.target.value)} className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-warranty" />
            </Field>
          </div>
        </Section>

        <Section title="Karosserie & Farben">
          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="Türen">
              <Select value={form.doors ? String(form.doors) : "0"} onValueChange={(v) => update("doors", v === "0" ? 0 : Number(v))}>
                <SelectTrigger className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-doors"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-950 text-white">
                  <SelectItem value="0">—</SelectItem>
                  {DOOR_OPTIONS.map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sitzplätze">
              <Select value={form.seats ? String(form.seats) : "0"} onValueChange={(v) => update("seats", v === "0" ? 0 : Number(v))}>
                <SelectTrigger className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-seats"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-950 text-white">
                  <SelectItem value="0">—</SelectItem>
                  {SEAT_OPTIONS.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Metallic">
              <button
                type="button"
                onClick={() => update("metallic", !form.metallic)}
                data-testid="form-metallic"
                className={`flex w-full items-center justify-between rounded-md border px-3 py-2.5 text-sm transition-colors ${form.metallic ? "border-red-600 bg-red-600/10 text-white" : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700"}`}
              >
                <span>{form.metallic ? "Ja" : "Nein"}</span>
                <span className={`grid h-4 w-4 place-items-center rounded-sm border ${form.metallic ? "border-red-600 bg-red-600" : "border-zinc-700"}`}>
                  {form.metallic && <Check className="h-3 w-3 text-white" />}
                </span>
              </button>
            </Field>
          </div>
          <Field label="Aussenfarbe">
            <ColorPicker options={EXTERIOR_COLORS} value={form.exterior_color} onChange={(v) => update("exterior_color", v)} testIdPrefix="form-ext" />
          </Field>
          <Field label="Innenfarbe">
            <ColorPicker options={INTERIOR_COLORS} value={form.interior_color} onChange={(v) => update("interior_color", v)} testIdPrefix="form-int" />
          </Field>
        </Section>

        <Section title="Verbrauch & Effizienz">
          <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-4">
            <Field label="Verbrauch (l/100km)">
              <Input type="number" step="0.1" value={form.fuel_consumption_l100km} onChange={(e) => update("fuel_consumption_l100km", e.target.value)} className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-consumption" />
            </Field>
            <Field label="CO₂ (g/km)">
              <Input type="number" value={form.co2_g_km} onChange={(e) => update("co2_g_km", e.target.value)} className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-co2" />
            </Field>
            <Field label="Batterie (kWh)">
              <Input type="number" step="0.1" value={form.battery_capacity_kwh} onChange={(e) => update("battery_capacity_kwh", e.target.value)} className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-battery" />
            </Field>
            <Field label="Elektro-Reichweite (km)">
              <Input type="number" value={form.electric_range_km} onChange={(e) => update("electric_range_km", e.target.value)} className="border-zinc-800 bg-zinc-950 text-white" data-testid="form-range" />
            </Field>
          </div>
        </Section>

        <Section title="Zustand & Verkäufer">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Zustand">
              <Select value={form.condition} onValueChange={(v) => update("condition", v)}>
                <SelectTrigger className="border-zinc-800 bg-zinc-950 capitalize text-white" data-testid="form-condition"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-950 text-white">
                  {CONDITIONS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Verkäufer-Typ">
              <Select value={form.seller_type} onValueChange={(v) => update("seller_type", v)}>
                <SelectTrigger className="border-zinc-800 bg-zinc-950 capitalize text-white" data-testid="form-seller-type"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-950 text-white">
                  {SELLER_TYPES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </Section>

        {/* Phase 68 — extended dealer-grade attributes */}
        <Section title="Fahrzeug-Historie & Zustand">
          <p className="-mt-2 mb-2 text-[11px] text-zinc-500">
            Alle Felder optional. Werden in der Inserats-Anzeige hervorgehoben und fliessen in die KI-Beschreibung ein.
          </p>
          <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3">
            <Field label="Letzte MFK">
              <Input
                type="date"
                value={form.mfk_valid_until || ""}
                onChange={(e) => update("mfk_valid_until", e.target.value)}
                className="border-zinc-800 bg-zinc-950 text-white"
                data-testid="form-mfk-valid"
              />
            </Field>
            <Field label="Letzter Service (Datum)">
              <Input
                type="date"
                value={form.last_service_date || ""}
                onChange={(e) => update("last_service_date", e.target.value)}
                className="border-zinc-800 bg-zinc-950 text-white"
                data-testid="form-last-service"
              />
            </Field>
            <Field label="Anzahl Schlüssel">
              {/* Phase 68.3 — stepper replaces broken type=number input ('01' bug) */}
              <NumberStepper
                value={Number(form.number_of_keys) || 1}
                onChange={(v) => update("number_of_keys", v)}
                min={1}
                max={10}
                testId="form-keys"
              />
            </Field>
            {(form.fuel === "elektro" || form.fuel === "hybrid") && (
              <Field label="Akku-Health (%)">
                <Input
                  type="number" min="0" max="100"
                  value={form.battery_health_pct || 0}
                  onChange={(e) => update("battery_health_pct", e.target.value)}
                  className="border-zinc-800 bg-zinc-950 text-white"
                  data-testid="form-battery-health"
                />
              </Field>
            )}
          </div>
          {/* Toggle-grid for boolean attributes */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <ToggleCheckbox
              checked={!!form.ab_mfk}
              onChange={(v) => update("ab_mfk", v)}
              label="Ab MFK"
              testId="form-ab-mfk"
            />
            <ToggleCheckbox
              checked={form.accident_free === true}
              onChange={(v) => update("accident_free", v ? true : null)}
              label="Unfallfrei"
              testId="form-accident-free"
            />
            <ToggleCheckbox
              checked={form.non_smoker === true}
              onChange={(v) => update("non_smoker", v ? true : null)}
              label="Nichtraucherfahrzeug"
              testId="form-non-smoker"
            />
            <ToggleCheckbox
              checked={form.service_book === true}
              onChange={(v) => update("service_book", v ? true : null)}
              label="Lückenloses Service-Heft"
              testId="form-service-book"
            />
            <ToggleCheckbox
              checked={!!form.new_tires}
              onChange={(v) => update("new_tires", v)}
              label="Neue Reifen"
              testId="form-new-tires"
            />
            <ToggleCheckbox
              checked={form.warranty_available === true}
              onChange={(v) => update("warranty_available", v ? true : null)}
              label="Garantie verfügbar"
              testId="form-warranty-available"
            />
          </div>
        </Section>

        {/* Boost / Featured (only available when editing an existing listing) */}
        {isEdit && currentListing && (
          <Section title={t.boost?.title || "Inserat hervorheben"}>
            <BoostCard
              t={t}
              isActive={isBoostActive}
              daysLeft={boostDaysLeft}
              expiresAt={currentListing.featured_until}
              busy={boostBusy}
              onBoost={handleBoost}
              onCancel={handleCancelBoost}
            />
          </Section>
        )}

        <Section title={t.create.media}>
          <div className="rounded-lg border border-dashed border-zinc-700 bg-zinc-950 p-8 text-center">
            <Upload className="mx-auto h-8 w-8 text-zinc-500" />
            <p className="mt-3 text-sm text-zinc-400">{t.create.drag}</p>
            <input id="file-upload" type="file" multiple accept="image/*" className="hidden" onChange={onUpload} data-testid="file-upload-input" />
            <Button type="button" disabled={uploading} onClick={() => document.getElementById("file-upload").click()} className="mt-4 bg-red-600 text-white hover:bg-red-500" data-testid="upload-button">
              {uploading ? "..." : t.create.upload}
            </Button>
          </div>
          {imagePaths.length > 0 && (
            <>
              <p className="text-xs text-zinc-500" data-testid="dnd-hint">
                Tipp: Erstes Bild = Titelbild. Ziehe (Touch: lang halten) zum Sortieren · Klick auf ✕ zum Löschen.
              </p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                <SortableContext items={imagePaths} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-4" data-testid="image-grid">
                    {imagePaths.map((p, i) => (
                      <SortableImageTile
                        key={p}
                        id={p}
                        index={i}
                        src={api.fileUrl(p)}
                        onRemove={() => removeImg(p)}
                      />
                    ))}
                  </div>
                </SortableContext>
                <DragOverlay dropAnimation={{ duration: 200 }}>
                  {activeDragId ? (
                    <div className="aspect-square overflow-hidden rounded border-2 border-red-500 shadow-2xl shadow-red-600/40 ring-2 ring-red-500/40">
                      <img src={api.fileUrl(activeDragId)} alt="" className="h-full w-full scale-[1.02] object-cover" />
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </>
          )}
        </Section>

        <Section title={t.create.desc}>
          <div className="space-y-4">
            {/* Phase 68 — Custom seller notes (fed into AI description) */}
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 sm:p-4">
              <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">
                Notizen für die KI-Beschreibung <span className="ml-1 text-[10px] normal-case tracking-normal text-amber-300/60">optional</span>
              </Label>
              <p className="mt-1 text-[11px] leading-relaxed text-zinc-400">
                Stichworte oder Sätze, die in die KI-Beschreibung einfliessen sollen. Z.B.: „Frisch ab grossem Service, Klimaanlage gewartet, garagiert, Erstbesitzer".
              </p>
              <Textarea
                rows={3}
                value={form.custom_notes || ""}
                onChange={(e) => update("custom_notes", e.target.value)}
                placeholder="Eigene Stichworte für die Beschreibung…"
                className="mt-2 border-amber-500/20 bg-zinc-950 text-white placeholder:text-zinc-600"
                data-testid="form-custom-notes"
                maxLength={500}
              />
              <div className="mt-1 text-right text-[10px] text-zinc-500">
                {(form.custom_notes || "").length} / 500
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3">
              <Button
                type="button"
                onClick={generateDescription}
                disabled={aiBusy || !form.brand || !form.model}
                size="sm"
                data-testid="ai-describe-button"
                className="group relative gap-2 overflow-hidden border border-red-500/40 bg-gradient-to-r from-red-600/20 via-red-500/15 to-orange-500/20 text-white shadow-[0_0_20px_-8px_rgba(220,38,38,0.6)] transition-all hover:from-red-600/30 hover:via-red-500/25 hover:to-orange-500/30 hover:shadow-[0_0_30px_-6px_rgba(220,38,38,0.8)] disabled:opacity-50 disabled:shadow-none"
              >
                {aiBusy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span data-testid="ai-loading">{t.ai?.generating || "Generiere..."}</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 text-red-300 transition-transform group-hover:scale-110" />
                    <span>{t.ai?.button || "KI-Beschreibung schreiben"}</span>
                  </>
                )}
              </Button>
            </div>
            <Textarea
              rows={9}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder={t.ai?.placeholder || "Beschreibung – oder klicke auf KI-Beschreibung schreiben für einen Vorschlag, den du danach beliebig anpassen kannst."}
              disabled={aiBusy}
              className="border-zinc-800 bg-zinc-950 text-white"
              data-testid="form-description"
            />
            <p className="text-[11px] text-zinc-500">
              {t.ai?.hint || "Der KI-Text basiert ausschliesslich auf den oben eingegebenen Daten. Du kannst ihn frei bearbeiten."}
            </p>
          </div>
        </Section>

        <Section title={t.create.equipment}>
          <p className="text-sm text-zinc-500">{features.length} ausgewählt</p>
          <div className="space-y-8">
            {FEATURE_CATEGORIES.map((cat) => (
              <div key={cat.id}>
                <h3 className="mb-4 text-sm font-semibold text-white">{cat.label.de}</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                  {cat.items.map((item) => {
                    const active = features.includes(item);
                    return (
                      <button
                        type="button"
                        key={item}
                        onClick={() => toggleFeature(item)}
                        data-testid={`feature-${item}`}
                        className={`flex items-center gap-3 rounded-md border px-3 py-2.5 text-left text-sm transition-all ${active ? "border-red-600 bg-red-600/10 text-white" : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700 hover:text-white"}`}
                      >
                        <span className={`grid h-4 w-4 flex-shrink-0 place-items-center rounded-sm border ${active ? "border-red-600 bg-red-600" : "border-zinc-700"}`}>
                          {active && <Check className="h-3 w-3 text-white" />}
                        </span>
                        <span className="truncate">{item}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title={t.create.contact}>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label={t.create.location}><Input value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="Zürich" className="border-zinc-800 bg-zinc-950 text-white" /></Field>
            <Field label={t.create.phone}><Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+41 79 ..." className="border-zinc-800 bg-zinc-950 text-white" /></Field>
          </div>
        </Section>

        <Button type="submit" disabled={busy || (!isEdit && !sub)} size="lg" className="w-full bg-red-600 text-white hover:bg-red-500 sm:w-auto" data-testid="submit-listing">
          {busy ? t.create.saving : (isEdit ? "Speichern" : t.create.submit)}
        </Button>
      </form>
    </div>
  );
}

function BoostCard({ t, isActive, daysLeft, expiresAt, busy, onBoost, onCancel }) {
  if (isActive) {
    return (
      <div className="relative overflow-hidden rounded-xl border border-red-500/40 bg-gradient-to-br from-red-950/30 via-zinc-950 to-orange-950/15 p-5" data-testid="boost-card-active">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-500 to-transparent" />
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-red-600 text-white shadow-lg shadow-red-600/30">
              <Crown className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded-full border border-red-500/40 bg-red-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-300">
                  {t.boost?.active || "Hervorgehoben"}
                </span>
                <span className="text-xs text-zinc-400">{daysLeft} {t.boost?.daysLeft || "Tage übrig"}</span>
              </div>
              <p className="mt-1 text-sm text-zinc-300">
                {t.boost?.until || "bis"} {new Date(expiresAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            className="border-zinc-800 bg-transparent text-zinc-300 hover:bg-zinc-900"
            data-testid="boost-cancel"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <X className="mr-2 h-4 w-4" />}
            {t.boost?.cancel || "Boost stoppen"}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="relative overflow-hidden rounded-xl border border-red-500/30 bg-gradient-to-br from-red-950/20 via-zinc-950 to-zinc-950 p-5 transition-all hover:border-red-500/50 hover:shadow-[0_0_30px_-12px_rgba(220,38,38,0.5)]" data-testid="boost-card-cta">
      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-red-600/10 blur-2xl" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-xl bg-gradient-to-br from-red-600 to-orange-500 text-white shadow-lg">
            <Rocket className="h-5 w-5" />
          </div>
          <div className="max-w-md">
            <h3 className="font-display text-lg font-semibold text-white">{t.boost?.title || "Inserat hervorheben"}</h3>
            <p className="mt-1 text-sm text-red-300">{t.boost?.subtitle || "Mehr Sichtbarkeit & schnellere Verkäufe"}</p>
            <p className="mt-2 text-xs leading-relaxed text-zinc-400">
              {t.boost?.description || "Dein Inserat erscheint zuoberst in den Suchresultaten und auf der Startseite mit Premium-Badge."}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-right">
            <div className="font-display text-2xl font-bold text-white">{t.boost?.price || "CHF 9"}</div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500">{t.boost?.duration || "7 Tage"}</div>
          </div>
          <Button
            type="button"
            onClick={onBoost}
            disabled={busy}
            className="bg-gradient-to-r from-red-600 to-orange-500 text-white hover:from-red-500 hover:to-orange-400"
            data-testid="boost-buy"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Rocket className="mr-2 h-4 w-4" />}
            {busy ? "Weiterleitung…" : (t.boost?.buyButton || "Jetzt hervorheben")}
          </Button>
        </div>
      </div>
      <p className="mt-4 text-[10px] uppercase tracking-wider text-zinc-500">
        🔒 {t.boost?.payments || "Bezahlung sicher via Stripe — Karte, Apple Pay, Google Pay, TWINT"}
      </p>
    </div>
  );
}

function SortableImageTile({ id, index, src, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Hide the original tile while dragging — DragOverlay shows the floating preview
    opacity: isDragging ? 0 : 1,
  };
  // Attach setNodeRef + listeners + attributes to the SAME outer div so dnd-kit's
  // droppable target and the activation source are the same element. This is the
  // canonical pattern for SortableContext + rectSortingStrategy. A separate inner
  // handle overlay breaks closestCenter collision detection in headless Chromium.
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-testid={`image-tile-${index}`}
      aria-label={`Bild ${index + 1} ziehen zum Sortieren`}
      className="group relative aspect-square cursor-grab touch-none select-none overflow-hidden rounded border border-zinc-800 bg-zinc-950 transition-shadow hover:border-zinc-700 hover:shadow-lg hover:shadow-black/40 active:cursor-grabbing"
    >
      {/* Hidden duplicate test handle so e2e tests can still target image-handle-N */}
      <span data-testid={`image-handle-${index}`} className="hidden" aria-hidden />

      <img src={src} alt="" className="pointer-events-none h-full w-full select-none object-cover" draggable={false} />

      {index === 0 && (
        <div className="pointer-events-none absolute left-1 top-1 z-10 inline-flex items-center gap-1 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white shadow-lg" data-testid="cover-badge">
          <Star className="h-3 w-3" /> Titel
        </div>
      )}
      <div className="pointer-events-none absolute right-1 top-1 z-10 grid h-7 w-7 place-items-center rounded-full bg-black/70 opacity-80 group-hover:opacity-100">
        <GripVertical className="h-3.5 w-3.5 text-white" />
      </div>

      {/* Delete button — must capture pointerdown BEFORE dnd-kit's listeners,
          and sit above the tile. We use onPointerDownCapture + stopPropagation. */}
      <button
        type="button"
        onPointerDownCapture={(e) => { e.stopPropagation(); }}
        onMouseDownCapture={(e) => { e.stopPropagation(); }}
        onTouchStartCapture={(e) => { e.stopPropagation(); }}
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        data-testid={`image-remove-${index}`}
        aria-label="Bild entfernen"
        style={{ pointerEvents: "auto" }}
        className="absolute bottom-1 right-1 z-20 grid h-7 w-7 place-items-center rounded-full bg-black/85 text-white opacity-100 transition-colors hover:bg-red-600 sm:opacity-0 sm:group-hover:opacity-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div className="pointer-events-none absolute bottom-1 left-1 z-10 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-200">
        {index + 1}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-5 border-b border-zinc-800 pb-12 last:border-0">
      <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{label}</Label>
      {children}
    </div>
  );
}

// Phase 68.3 — mobile-friendly +/- stepper. Fixes the "01" leading-zero
// editing bug on type=number inputs and provides large touch targets.
function NumberStepper({ value, onChange, min = 0, max = 99, testId }) {
  const v = Math.max(min, Math.min(max, Number(value) || min));
  const dec = () => onChange(Math.max(min, v - 1));
  const inc = () => onChange(Math.min(max, v + 1));
  return (
    <div className="flex items-stretch overflow-hidden rounded-md border border-zinc-800 bg-zinc-950" data-testid={testId}>
      <button
        type="button"
        onClick={dec}
        disabled={v <= min}
        aria-label="weniger"
        data-testid={`${testId}-dec`}
        className="grid h-11 w-11 flex-shrink-0 place-items-center text-lg font-bold text-zinc-300 hover:bg-zinc-900 disabled:opacity-30 disabled:hover:bg-transparent"
      >−</button>
      <div className="flex flex-1 items-center justify-center border-x border-zinc-800 text-center text-base font-semibold text-white" data-testid={`${testId}-value`}>
        {v}
      </div>
      <button
        type="button"
        onClick={inc}
        disabled={v >= max}
        aria-label="mehr"
        data-testid={`${testId}-inc`}
        className="grid h-11 w-11 flex-shrink-0 place-items-center text-lg font-bold text-zinc-300 hover:bg-zinc-900 disabled:opacity-30 disabled:hover:bg-transparent"
      >+</button>
    </div>
  );
}

// Phase 68 — pill-style boolean toggle for dealer attribute grid
function ToggleCheckbox({ checked, onChange, label, testId }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
        checked
          ? "border-red-500/60 bg-red-600/10 text-white"
          : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700 hover:text-white"
      }`}
    >
      <span
        className={`grid h-4 w-4 flex-shrink-0 place-items-center rounded border ${
          checked ? "border-red-500 bg-red-600 text-white" : "border-zinc-700 bg-zinc-950"
        }`}
        aria-hidden
      >
        {checked && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>
      <span className="leading-tight">{label}</span>
    </button>
  );
}

function ColorPicker({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => onChange("")}
        data-testid={`${testIdPrefix}-any`}
        className={`grid h-9 w-9 place-items-center rounded-full border text-xs uppercase transition-colors ${!value ? "border-red-600 text-white" : "border-zinc-800 text-zinc-500 hover:text-white"}`}
        title="Keine"
      >∗</button>
      {options.map((c) => {
        const active = value === c.value;
        return (
          <button
            key={c.value}
            type="button"
            onClick={() => onChange(c.value)}
            title={c.label}
            data-testid={`${testIdPrefix}-${c.value}`}
            className={`relative h-9 w-9 rounded-full border-2 transition-transform hover:scale-110 ${active ? "border-red-600 ring-2 ring-red-600/40 ring-offset-2 ring-offset-black" : "border-zinc-800"}`}
            style={{ backgroundColor: c.hex }}
          >
            {active && (
              <Check className={`absolute inset-0 m-auto h-4 w-4 ${c.hex === "#f4f4f5" || c.hex === "#cdb89a" || c.hex === "#facc15" ? "text-black" : "text-white"}`} />
            )}
          </button>
        );
      })}
    </div>
  );
}
